import React from 'react'
import { t } from 'i18next'
import {
  Add as AddIcon,
  Block as DeactivateIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material'
import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useDispatch } from '../store'
import { PrivatePage } from '../component/PagePrivate'
import { PopupAsk } from '../component/PopupAsk'
import { DataTable } from '../component/datatable'
import { useTableStyles } from '../component/datatable/hook/useStyles'
import { TableRef } from '../component/datatable/types'
import { formatDate } from '../function/date'
import { deactivateQrRegistration } from '../store/qrRegistration/actionCreator'
import { QrRegistrationResult } from '../store/qrRegistration/actionType'
import { PopupCreateQrRegistration } from '../container/qrRegistration/PopupCreateQrRegistration'
import { downloadQrRegistrationPdf } from '../container/qrRegistration/downloadQrRegistrationPdf'

// ---------------------------------------------------------------------------
// QR quick-registration links — printable QR codes that take a person
// straight to the dashboard's minimal phone-only signup screen (`/join`).
// Mirrors EnterprisePage.tsx's shape (title + Create button + DataTable),
// but the DataTable here talks directly to /auth/qr-registration rather
// than to a Subscriber-shaped endpoint, since these rows aren't users.
// ---------------------------------------------------------------------------

type QrDisplayStatus = 'active' | 'inactive' | 'scheduled' | 'expired'

const STATUS_COLOR: Record<QrDisplayStatus, 'success' | 'default' | 'warning' | 'error'> = {
  active: 'success',
  inactive: 'default',
  scheduled: 'warning',
  expired: 'error',
}

// isActive only tracks the admin's on/off switch — a link can still be
// outside its validFrom/validUntil window while isActive stays true (see
// AuthService#validateQrRegistration on the backend, which checks both),
// so the table's status needs to reflect that window too, not just the flag.
function getQrDisplayStatus(row: QrRegistrationResult): QrDisplayStatus {
  if (!row.isActive) return 'inactive'

  const now = Date.now()
  if (row.validFrom && now < new Date(row.validFrom).getTime()) return 'scheduled'
  if (row.validUntil && now > new Date(row.validUntil).getTime()) return 'expired'
  return 'active'
}

export function QrRegistrationPage() {
  const dispatch = useDispatch()
  const classesTable = useTableStyles()

  const tableRef = React.useRef<TableRef>(null)
  const [createOpen, setCreateOpen] = React.useState(false)
  const [deactivateRow, setDeactivateRow] = React.useState<QrRegistrationResult | null>(null)
  const [downloadingId, setDownloadingId] = React.useState<number | null>(null)

  function updateTable() {
    tableRef.current?.refresh()
  }

  const tableHeaderData = [
    { key: 'label', text: t('qrRegistration.table.label') },
    { key: 'joinUrl', text: t('qrRegistration.table.joinUrl') },
    { key: 'status', text: t('qrRegistration.table.status') },
    { key: 'validity', text: t('qrRegistration.table.validity') },
    { key: 'created', text: t('qrRegistration.table.created') },
    { key: 'action', text: t('qrRegistration.table.action') },
  ]

  async function handleDownload(row: QrRegistrationResult) {
    setDownloadingId(row.id)
    try {
      await downloadQrRegistrationPdf(row.id)
    } finally {
      setDownloadingId(null)
    }
  }

  function handleDeactivate() {
    if (!deactivateRow) return
    dispatch(deactivateQrRegistration(deactivateRow.id, updateTable))
  }

  function TableBody(row: QrRegistrationResult, idx: number) {
    const status = getQrDisplayStatus(row)

    return (
      <TableRow key={row.id} className={idx % 2 !== 0 ? classesTable.rowEven : ''}>
        <TableCell classes={classesTable}>{row.label || ''}</TableCell>
        <TableCell classes={classesTable}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }} noWrap>
            {row.joinUrl}
          </Typography>
        </TableCell>
        <TableCell classes={classesTable}>
          <Chip
            label={t(`qrRegistration.status.${status}`)}
            color={STATUS_COLOR[status]}
            size="small"
          />
        </TableCell>
        <TableCell classes={classesTable}>
          {row.validFrom || row.validUntil ? (
            <Typography sx={{ fontSize: 12 }} noWrap>
              {row.validFrom ? formatDate(row.validFrom, 'dd/MM/yyyy HH:mm') : t('qrRegistration.table.noStart')}
              {' – '}
              {row.validUntil ? formatDate(row.validUntil, 'dd/MM/yyyy HH:mm') : t('qrRegistration.table.noEnd')}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12 }} color="textSecondary">
              {t('qrRegistration.table.noExpiry')}
            </Typography>
          )}
        </TableCell>
        <TableCell classes={classesTable}>
          {formatDate(row.createdTime, 'dd/MM/yyyy HH:mm:ss')}
        </TableCell>
        <TableCell classes={classesTable}>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={<span>{t('qrRegistration.action.downloadPdf')}</span>}>
              <IconButton onClick={() => handleDownload(row)} disabled={downloadingId === row.id}>
                {downloadingId === row.id ? <CircularProgress size={18} /> : <PdfIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            {row.isActive && (
              <Tooltip title={<span>{t('qrRegistration.action.deactivate')}</span>}>
                <IconButton onClick={() => setDeactivateRow(row)}>
                  <DeactivateIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <PrivatePage>
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Stack>
          <Typography variant="semiBold" fontSize={24}>
            {t('qrRegistration.title')}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 560 }}>
            {t('qrRegistration.hint')}
          </Typography>
        </Stack>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          {t('qrRegistration.create.button')}
        </Button>
      </Stack>

      <DataTable
        id="qrRegistration"
        className={classesTable.card}
        ref={tableRef}
        url="/auth/qr-registration"
        headerData={tableHeaderData}
        renderBody={TableBody}
        emptyText="No QR registration links yet"
      />

      {createOpen && (
        <PopupCreateQrRegistration
          onClose={() => setCreateOpen(false)}
          onCreated={updateTable}
        />
      )}

      {deactivateRow && (
        <PopupAsk
          header={t('qrRegistration.action.deactivateConfirm')}
          onYes={handleDeactivate}
          onClose={() => setDeactivateRow(null)}
        />
      )}
    </PrivatePage>
  )
}
